/* eslint-disable react/jsx-no-bind */
import React, { memo, FC, useCallback, useEffect } from "react";
import cn from "classnames";
import ComboxList from "~/components/shared/Combox/ComboxList";
import ComboxItem from "~/components/shared/Combox/ComboxItem";
import ComboxFilterInput from "~/components/shared/Combox/ComboxFilterInput";
import { useComboxContext } from "~/components/shared/Combox/ComboxContext";
import { ProcessedEmoji } from "~/components/shared/NavatarPicker/index";

const EmojiGridWithClearHighlight: FC<
  Omit<React.ComponentProps<typeof ComboxList>, "onMouseLeave"> & { onMouseLeave?: () => void }
> = ({ onMouseLeave, ...props }) => {
  const { setHighlightedIndex } = useComboxContext();

  const handleMouseLeave = useCallback(() => {
    setHighlightedIndex(-1);
    if (onMouseLeave) {
      onMouseLeave();
    }
  }, [onMouseLeave, setHighlightedIndex]);

  return <ComboxList {...props} onMouseLeave={handleMouseLeave} />;
};

export interface EmojiGridSectionProps {
  emojis: ProcessedEmoji[];
  selectedEmoji?: string | null;
  onHoveredEmojiChange: (emoji: ProcessedEmoji | null) => void;
  getEmojiSrc: (emoji: ProcessedEmoji) => string;
  isOpen?: boolean;
}

const EmojiGridSection: FC<EmojiGridSectionProps> = memo(
  ({ emojis, selectedEmoji, onHoveredEmojiChange, getEmojiSrc, isOpen }) => {
    const { searchInputRef, searchQuery } = useComboxContext();
    const emojiList = searchQuery ? emojis : emojis;

    // Focus the search input when the popover opens (gotta do with force mounting)
    useEffect(() => {
      if (isOpen && searchInputRef.current) {
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 0);
      }
    }, [isOpen, searchInputRef]);

    const handleHover = useCallback(
      (emoji: ProcessedEmoji) => {
        onHoveredEmojiChange(emoji);
      },
      [onHoveredEmojiChange],
    );

    const clearHover = useCallback(() => {
      onHoveredEmojiChange(null);
    }, [onHoveredEmojiChange]);

    return (
      <>
        <ComboxFilterInput
          placeholder="Filter emojis..."
          data-testid="emoji-search-input"
          gridColumns={9}
        />
        <div className="h-[300px] scroll-py-6 overflow-y-auto" role="listbox">
          <EmojiGridWithClearHighlight
            className="grid gap-3 p-2"
            emptyState={
              <div className="dark-gray col-span-9 flex h-[200px] w-full flex-col items-center justify-center gap-2 px-4 py-6 text-center text-sm">
                No emoji found
              </div>
            }
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(32px, 1fr))" }}
            onMouseLeave={clearHover}
          >
            {emojiList.map((emoji, index) => {
              const emojiKey = `:${emoji.name}:`;
              const isSelected = emojiKey === selectedEmoji;

              return (
                <ComboxItem
                  key={`${emoji.source}-${emoji.name}`}
                  value={emojiKey}
                  itemIndex={index}
                  className={cn(
                    "flex h-8 w-8 cursor-pointer items-center justify-center rounded-sm !p-0",
                    "hover:bg-gray-300 focus:bg-gray-300",
                    isSelected && "bg-gray-300",
                  )}
                  onMouseEnter={() => handleHover(emoji)}
                  onMouseLeave={clearHover}
                  onFocus={() => handleHover(emoji)}
                  onBlur={clearHover}
                  aria-selected={isSelected}
                  data-testid={`emoji-button-${emoji.name}`}
                >
                  <span className="sr-only">
                    {emoji.name} {emoji.aliases?.join(" ")}
                  </span>
                  {emoji.unicode ? (
                    <span className="text-xl">{emoji.unicode}</span>
                  ) : emoji.image ? (
                    <img
                      alt={emoji.name}
                      src={getEmojiSrc(emoji)}
                      className="h-5 w-5"
                      draggable={false}
                      loading="lazy"
                    />
                  ) : null}
                </ComboxItem>
              );
            })}
          </EmojiGridWithClearHighlight>
        </div>
      </>
    );
  },
);

export default EmojiGridSection;
